/**
 *  节点模式，自动保存
*/
import { saveWorkFlow } from '$/domains/flowCanvas/flowCanvasService';
import { debounce } from 'lodash';
import { FreeLayoutPluginContext, WorkflowContentChangeEvent, WorkflowContentChangeType } from '@flowgram.ai/free-layout-editor';
import { editRoomInfo, ProjectType, submitRoom } from '$/api/server';
import useFlowStore, { setHasNodeOperation, flowMtstatTrack } from '$/store/useFlowStore';
import { addCommonParams } from '$/api/helpers';
import { saveWorkFlow as saveSopWorkFlow } from '$/domains/sop/sopService';
import useSopStore from '$/store/useSopStore';

function mtstat(ctx: FreeLayoutPluginContext) {
  const data = ctx.document.toJSON();
  const node_edit_list = data.nodes.filter((node) => (node.type as string).includes('EDIT_NODE'));
  const node_output_list = node_edit_list.map((item) => {
    return (item.data.childrenNodeList as string[]);
  }).flat().filter((id) => !!id);
  const node_input_list = data.nodes.filter((node) => !(node.type as string).includes('EDIT_NODE'));
  flowMtstatTrack('workflow_node_num', {
    node_total_num: data.nodes.length,
    node_edit_list: node_edit_list.map((item) => item.id).join(','),
    node_edit_num: node_edit_list.length,
    node_output_list: node_output_list.join(','),
    node_output_num: node_output_list.length,
    node_input_list: node_input_list.map((item) => item.id).join(','),
    node_input_num: node_input_list.length,
  });
}

const mtstatDebounceFn = debounce(mtstat, 300);

/**
 * 立即将工作流画布 JSON 落盘到 `/workflow/canvas/save.json`（与自动保存相同过滤与附带逻辑，不经防抖）。
 * 用于异步写入 `assetId` 等不经过 `onContentChange` 的路径，避免服务端缺字段。
 * @param ctx 工作流布局上下文
 */
export async function persistWorkFlowCanvas(ctx: FreeLayoutPluginContext): Promise<void> {
  const data = ctx.document.toJSON();
  const preIdList: string[] = [];
  data.nodes = data.nodes.filter((node) => {
    if (node?.data?.isPre) {
      preIdList.push(node.id);
      return false;
    }
    return !node?.data?.isPre;
  });
  data.nodes.forEach((node) => {
    if (node?.data?.childrenNodeList) {
      // eslint-disable-next-line no-param-reassign
      node.data.childrenNodeList = node.data.childrenNodeList.filter((id: string) => !preIdList.includes(id));
    }
    // 过滤 group 节点中的 blockIDs，排除 pre 节点
    if (node?.data?.blockIDs) {
      // eslint-disable-next-line no-param-reassign
      node.data.blockIDs = node.data.blockIDs.filter((id: string) => !preIdList.includes(id));
    }
  });

  console.log('Saving data:', data);
  // 先调用保存接口
  const room_id = useFlowStore.getState().roomId;
  if (useSopStore.getState().fromAdmin) {
    saveSopWorkFlow({
      room_id,
      data: JSON.stringify(data),
    });
  } else {
    saveWorkFlow({
      room_id,
      data: JSON.stringify(data),
    });
  }

  // 如果用户还没有操作过节点，则提交房间信息
  const { hasNodeOperation } = useFlowStore.getState();
  if (!hasNodeOperation) {
    setHasNodeOperation(true);
    await submitRoom({ room_id, room_type: ProjectType.Workflow });
  }

  // 项目更新
  const body = {
    parameter: {
      ...addCommonParams(),
      room_id,
      path_scene: 'historymodify',
    },
  };
  editRoomInfo(body);
}

// 保存节点画布数据（防抖）
async function saveNodeDebounce(ctx: FreeLayoutPluginContext) {
  await persistWorkFlowCanvas(ctx);
}

const debounceFn = debounce(saveNodeDebounce, 300);

export function forceSave(ctx: FreeLayoutPluginContext) {
  saveNodeDebounce(ctx);
}

export function autoSaveHandler(ctx: FreeLayoutPluginContext, event: WorkflowContentChangeEvent) {
  // pre节点不保存
  if (event.toJSON().data?.isPre) return;
  switch (event.type) {
    // 事件触发过于频繁，增加防抖
    case WorkflowContentChangeType.MOVE_NODE:
    case WorkflowContentChangeType.NODE_DATA_CHANGE:
    case WorkflowContentChangeType.DELETE_NODE:
    case WorkflowContentChangeType.DELETE_LINE:
    case WorkflowContentChangeType.ADD_NODE:
    case WorkflowContentChangeType.ADD_LINE:
      debounceFn(ctx);
      break;

    default:
      saveNodeDebounce(ctx);
      break;
  }

  if (event.type === WorkflowContentChangeType.DELETE_NODE || event.type === WorkflowContentChangeType.ADD_NODE) {
    mtstatDebounceFn(ctx);
  }
}
